/**
 * DiffProposal — agent-proposed change card shown in the chat tab (CR-D §3.5).
 *
 * Rendered below an agent message when the chat endpoint returns `proposed_content`.
 * "Accept Changes" persists the proposal via PATCH; "Discard" removes it with no API call.
 * The unified diff is shown/hidden inline via DiffViewer.
 *
 * ── TWO ACCEPT PATHS, AND THEY ARE NOT INTERCHANGEABLE (P0 redesign item D7) ─────────────────────
 * By default this card accepts via `usePatchArtifact` — the legacy per-artifact PATCH, correct for the
 * legacy per-file chat it was built for.
 *
 * A unified-chat result MUST pass {@link DiffProposalProps.onAccept} instead, routing through
 * `PATCH /api/projects/{projectId}/chat`. The difference is not stylistic: the legacy path knows
 * nothing about `project_chat_message_results`, so using it for a unified result would skip the
 * content-hash staleness check (silently overwriting a newer edit), never record
 * `resolution='accepted'`, and leave the proposal open in the transcript forever.
 */

import { useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { usePatchArtifact } from "@/hooks/useArtifacts";
import { DiffViewer } from "./DiffViewer";

export interface DiffProposalProps {
  proposedContent: string;
  diff: string;
  agentNote: string;
  artifactType: string;
  projectId: string;
  onAccepted: (newContent: string) => void;
  onDiscarded: () => void;
  /**
   * Overrides the accept action. **Required for unified-chat results** — see the module docstring.
   * Throwing from here leaves the card open, which is the correct behaviour for a refused accept
   * (e.g. a 409 because the proposal went stale).
   */
  onAccept?: () => Promise<void>;
  /** Replaces the "Discard" label — unified chat REJECTS server-side rather than discarding locally. */
  discardLabel?: string;
  /**
   * Replaces the "Accept Changes" label.
   *
   * Added for Item 4 (§5.4c): a proposal against a `committed` artifact is labelled
   * "Accept — start new version", because the button does something materially different there —
   * it moves a signed-off document back into draft. The label is part of the disclosure, not
   * decoration.
   */
  acceptLabel?: string;
  /**
   * Rendered ABOVE the Accept/Reject controls.
   *
   * Position is load-bearing: a disclosure about what Accept will do has to be readable before the
   * button is reached, not under it. Used by `ResultRow` for the `startsNewVersion` notice.
   */
  notice?: React.ReactNode;
  /** Extra line above the buttons, e.g. the artifact name in a multi-file turn. */
  headerSuffix?: string;
  /**
   * Hides Accept / Reject (the diff stays viewable). Set for viewers who may not resolve the
   * proposal — e.g. a non-member of the project, whose PATCH the server answers 403.
   */
  readOnly?: boolean;
}

export function DiffProposal({
  proposedContent,
  diff,
  agentNote,
  artifactType,
  projectId,
  onAccepted,
  onDiscarded,
  onAccept,
  discardLabel,
  acceptLabel,
  notice,
  headerSuffix,
  readOnly = false,
}: DiffProposalProps): JSX.Element {
  const { mutateAsync: patchArtifact, isPending: isPatching } =
    usePatchArtifact(projectId, artifactType);
  const [showDiff, setShowDiff] = useState<boolean>(false);
  const [isAccepting, setIsAccepting] = useState<boolean>(false);
  const isPending = isPatching || isAccepting;

  const handleAccept = async (): Promise<void> => {
    if (onAccept) {
      setIsAccepting(true);
      try {
        await onAccept();
        onAccepted(proposedContent);
      } catch {
        // The caller surfaces the error (a 409 stale/locked refusal is expected, not exceptional).
        // The card stays open deliberately so the user can see why and retry.
      } finally {
        setIsAccepting(false);
      }
      return;
    }

    try {
      await patchArtifact(proposedContent);
      toast.success("Changes accepted");
      onAccepted(proposedContent);
    } catch {
      // Global axios interceptor surfaces the error toast.
    }
  };

  return (
    <div className="mt-2 rounded-md border border-outlines bg-card p-3 text-card-foreground">
      <div className="flex items-center gap-2 text-sm font-medium">
        <FileText className="h-4 w-4 text-primary" aria-hidden="true" />
        Agent proposed changes
        {headerSuffix && (
          <span className="text-text-secondary">— {headerSuffix}</span>
        )}
      </div>

      {agentNote && (
        <p className="mt-1 text-sm italic text-text-secondary">{agentNote}</p>
      )}

      {/* Above the controls on purpose — see the `notice` prop docs. */}
      {notice}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowDiff((prev) => !prev)}
          aria-expanded={showDiff}
        >
          {showDiff ? "Hide Diff" : "Show Diff"}
        </Button>
        {!readOnly && (
          <>
            <Button
              size="sm"
              onClick={() => void handleAccept()}
              disabled={isPending}
            >
              {isPending && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {acceptLabel ?? "Accept Changes"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDiscarded}
              disabled={isPending}
            >
              {discardLabel ?? "Discard"}
            </Button>
          </>
        )}
      </div>

      {showDiff && <DiffViewer diff={diff} className="mt-3" />}
    </div>
  );
}

export default DiffProposal;
